import RefreshIcon from "@mui/icons-material/Refresh";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { GetUsersDetailByRoleParams } from "../../../../../api/services/user/models";
import { useGetUsersDetailByRoleMutation } from "../../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiGrid,
  MuiMultiSelect,
} from "../../../../../components";
import { ROLE_IDS } from "../../../../../helpers/constants";
import {
  getServiceOfficeCountryCodes,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { convertFunctionsOrCoachToDropdown } from "../../../../createProgram/helper";
import { SearchCoachReportFormKeys } from "../../../modelsAndSchemas/models";
import { PendingBillingSearchFormWrapper } from "../../../ReportSearchStyle";
import { reportsSearchReportsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

export interface CoachReportFormRef {
  reset: () => void;
}

export interface CoachReportFormProps {
  onResetSearch: () => void;
}

export const CoachReportForm = forwardRef<
  CoachReportFormRef,
  CoachReportFormProps
>(({ onResetSearch }, ref) => {
  const { gridSizeMedium: gridSize } = useGridContainer();
  const { fetchComponentTitle, commonLabels, useFetchLabel } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const coachRef = useRef<{ resetValues: (values?: string[]) => void }>(null);
  const { loggedInUser } = useGetLoggedInUserDetails();

  const { isMobile } = useMediaQueries();

  const {
    mutateAsync: getCoachList,
    data: coachList,
    isPending: isCoachesListPending,
    isError: isCoachesListError,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    const countryCodeListString = getServiceOfficeCountryCodes(loggedInUser);
    if (isNotEmpty(countryCodeListString)) {
      const params: GetUsersDetailByRoleParams = {
        roleIds: [
          ROLE_IDS.deliveryCoach,
          ROLE_IDS.coachAdmin,
          ROLE_IDS.coach,
          ROLE_IDS.leadConsultant,
        ],
        countryCode: countryCodeListString,
      };
      getCoachList(params);
    }
  }, [loggedInUser?.role]);

  const { reset, control } = useFormContext();
  const dispatch = useDispatch();

  useImperativeHandle(ref, () => ({
    reset: handleClearFilterEvent,
  }));

  useEffect(() => {
    if (isCoachesListPending) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isCoachesListPending]);

  useEffect(() => {
    if (isCoachesListError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isCoachesListError]);

  const handleClearFilterEvent = () => {
    reset({
      [SearchCoachReportFormKeys.coaches]: [],
    });
    coachRef.current?.resetValues([]);
    onResetSearch();
  };

  const getLabel = useFetchLabel(reportsSearchReportsSiteCoreKeys.header);

  const renderCoachFormHeader = () => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid justifySelf="end" className="form-header-right">
          <MuiButtonTertiary
            data-testid="reset-button"
            onClick={handleClearFilterEvent}
          >
            <RefreshIcon /> {commonLabels.resetAllSearch}
          </MuiButtonTertiary>
        </MuiGrid>
      </MuiGrid>
    );
  };

  return (
    <PendingBillingSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={true}
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(reportsSearchReportsSiteCoreKeys.header)}
          />
        }
      >
        {renderCoachFormHeader()}
        <MuiBox className="selectReportFieldContainer">
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 4}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={SearchCoachReportFormKeys.coaches}
                ref={coachRef}
              >
                <MuiMultiSelect
                  options={convertFunctionsOrCoachToDropdown(coachList)}
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.coachesDropDown,
                  )}
                  showSearch
                  showChip
                  id={SearchCoachReportFormKeys.coaches}
                  chipId={`${SearchCoachReportFormKeys.coaches}-chip`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                  isMandatory
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="selectReportButtonContainer" textAlign="right">
          <MuiButtonPrimary type="submit">
            {commonLabels.search}
          </MuiButtonPrimary>
        </MuiBox>
      </AccordionHeader>
    </PendingBillingSearchFormWrapper>
  );
});
